import { useState } from "react";
import { Button } from "@cntrl/ui";
import { useAuth } from "../../app/auth/useAuth.js";
import { AuthError } from "../../app/auth/session.js";
import { AuthCard } from "./AuthCard.js";
import styles from "./AuthForm.module.scss";

export interface VerifyEmailScreenProps {
  /** The single-use token from the verification link (`/verify-email?token=...`). */
  token: string;
  /** Leave for /login — the same way out every other auth card offers. */
  onDone: () => void;
}

/**
 * Confirm an email address from its link. Built 2026-09-06 (ART-7) because the LINK EXISTED AND THE PAGE DID
 * NOT: `IAuthService` has emailed `{AppBaseUrl}/verify-email?token=…` since the flow shipped, and the router
 * defined no such route, so every verification link landed on the SPA's not-found. It went unnoticed because
 * the flow is dormant at BOTH ends — nothing in the client calls `POST /auth/send-verification`, so the mail
 * is never actually sent. That is why this is a screen and not a feature: it makes the existing link correct,
 * and whoever wires the "resend verification" affordance later gets a destination that already works.
 *
 * THE TOKEN IS CONSUMED ON A PRESS, NEVER ON LOAD. It is single-use, and mail scanners and link-preview bots
 * follow URLs in email before a person does — verifying on mount would let a scanner burn the token and hand
 * the user a dead link. `/reset` makes the same choice for the same reason; there the form supplies the
 * pause naturally, here the button has to.
 *
 * Signed-out is the ORDINARY case (the link is opened from a mail client on whatever device is to hand), so
 * nothing here reads session state and the route sits outside the shell.
 */
export function VerifyEmailScreen({ token, onDone }: VerifyEmailScreenProps) {
  const auth = useAuth();
  const [state, setState] = useState<"idle" | "busy" | "done">("idle");
  const [serverError, setServerError] = useState<string | null>(null);

  if (!token) {
    return (
      <AuthCard
        title="Link expired"
        subtitle="This confirmation link is missing or has already been used."
        footer={
          <Button variant="link" type="button" onClick={onDone}>
            Back to sign in
          </Button>
        }
      >
        <span />
      </AuthCard>
    );
  }

  if (state === "done") {
    return (
      <AuthCard
        title="Email confirmed"
        subtitle="Your address is verified. You can sign in as usual."
        footer={
          <Button variant="link" type="button" onClick={onDone}>
            Back to sign in
          </Button>
        }
      >
        <span />
      </AuthCard>
    );
  }

  const onConfirm = async () => {
    setServerError(null);
    setState("busy");
    try {
      await auth.verifyEmail(token);
      setState("done");
    } catch (err) {
      setState("idle");
      // The server answers one uniform 400 for absent / used / expired — deliberately, so the endpoint is
      // not an oracle for which tokens exist. So the copy names the ACTION to take, not the cause.
      setServerError(
        err instanceof AuthError
          ? "That confirmation link is no longer valid. Sign in and request a new one."
          : "Something went wrong on this device — check your connection and try again.",
      );
    }
  };

  return (
    <AuthCard
      title="Confirm your email"
      subtitle="One tap and this address is verified on your Artist CNTRL account."
      footer={
        <Button variant="link" type="button" onClick={onDone}>
          Back to sign in
        </Button>
      }
    >
      <div className={styles.form}>
        {serverError && (
          <p className={styles.serverError} role="alert">
            {serverError}
          </p>
        )}
        <Button variant="accent" type="button" fullWidth loading={state === "busy"} onClick={onConfirm}>
          Confirm email
        </Button>
      </div>
    </AuthCard>
  );
}
